import React, { useState } from 'react';
import Button from '../../../shared/components/Button';

const CreateAccountScreen = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    whatsappNumber: data?.whatsappNumber || '',
    smsNumber: data?.smsNumber || '',
    email: data?.email || '',
    notificationPreference: data?.notificationPreference || 'whatsapp_only',
    // Optional payment handles (direct groups) — where others send you your pot
    // during your collecting turn. Editable later in settings.
    zelleIdentifier: data?.zelleIdentifier || '',
    cashappHandle: data?.cashappHandle || '',
    venmoHandle: data?.venmoHandle || ''
  });
  const [errors, setErrors] = useState({});

  const notificationOptions = [
    { value: 'whatsapp_only', label: 'WhatsApp only' },
    { value: 'whatsapp_sms', label: 'WhatsApp + SMS' },
    { value: 'sms_only', label: 'SMS only' },
    { value: 'in_app_only', label: 'In-app only' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'Enter your first name';
    if (!formData.lastName.trim()) newErrors.lastName = 'Enter your last name';

    if (!formData.whatsappNumber.trim()) {
      newErrors.whatsappNumber = 'WhatsApp number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.whatsappNumber)) {
      newErrors.whatsappNumber = 'Please enter a valid phone number';
    }
    
    if (!formData.smsNumber.trim()) {
      newErrors.smsNumber = 'SMS number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.smsNumber)) {
      newErrors.smsNumber = 'Please enter a valid phone number';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required to set up your account';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Carry a combined display name forward too, so downstream code that reads a
      // single `name` keeps working; first/last remain authoritative.
      onNext({ ...formData, name: `${formData.firstName.trim()} ${formData.lastName.trim()}` });
    }
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
          Create Your Account
        </h1>
        <p className="text-neutral-600">
          Tell us about yourself so we can set up your profile
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 space-y-6">
        {/* Legal name — first + last (becomes your name of record and signature) */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">First name *</label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="First"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.firstName ? 'border-error-500' : 'border-neutral-300'}`}
            />
            {errors.firstName && <p className="mt-1 text-sm text-error-600">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">Last name *</label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Last"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.lastName ? 'border-error-500' : 'border-neutral-300'}`}
            />
            {errors.lastName && <p className="mt-1 text-sm text-error-600">{errors.lastName}</p>}
          </div>
        </div>

        {/* WhatsApp Number */}
        <div>
          <label htmlFor="whatsappNumber" className="block text-sm font-medium text-neutral-700 mb-2">
            WhatsApp Number *
          </label>
          <input
            type="tel"
            id="whatsappNumber"
            value={formData.whatsappNumber}
            onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
            placeholder="+1 (555) 123-4567"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.whatsappNumber ? 'border-error-500' : 'border-neutral-300'
            }`}
          />
          {errors.whatsappNumber && (
            <p className="mt-1 text-sm text-error-600">{errors.whatsappNumber}</p>
          )}
        </div>

        {/* SMS Number */}
        <div>
          <label htmlFor="smsNumber" className="block text-sm font-medium text-neutral-700 mb-2">
            SMS Number *
          </label>
          <input
            type="tel"
            id="smsNumber"
            value={formData.smsNumber}
            onChange={(e) => handleInputChange('smsNumber', e.target.value)}
            placeholder="+1 (555) 123-4567"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.smsNumber ? 'border-error-500' : 'border-neutral-300'
            }`}
          />
          {errors.smsNumber && (
            <p className="mt-1 text-sm text-error-600">{errors.smsNumber}</p>
          )}
          <p className="mt-1 text-xs text-neutral-500">
            May differ from WhatsApp number
          </p>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
            Email <span className="text-error-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your@email.com"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.email ? 'border-error-500' : 'border-neutral-300'
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error-600">{errors.email}</p>
          )}
        </div>

        {/* Payment Details (optional; direct-payment groups) — shown to members
            paying you only during your collecting turn. Editable later in settings. */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Payment Details <span className="text-xs text-neutral-500">(optional)</span>
          </label>
          <input
            type="text"
            value={formData.zelleIdentifier}
            onChange={(e) => handleInputChange('zelleIdentifier', e.target.value)}
            placeholder="Zelle (email or phone)"
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
          />
          <input
            type="text"
            value={formData.cashappHandle}
            onChange={(e) => handleInputChange('cashappHandle', e.target.value)}
            placeholder="Cash App ($cashtag)"
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
          />
          <input
            type="text"
            value={formData.venmoHandle}
            onChange={(e) => handleInputChange('venmoHandle', e.target.value)}
            placeholder="Venmo (@handle)"
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Notification Preference */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Notification Preference
          </label>
          <div className="space-y-3">
            {notificationOptions.map(option => (
              <div
                key={option.value}
                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                  formData.notificationPreference === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
                onClick={() => handleInputChange('notificationPreference', option.value)}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="notificationPreference"
                    value={option.value}
                    checked={formData.notificationPreference === option.value}
                    onChange={() => handleInputChange('notificationPreference', option.value)}
                    className="mr-3 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-neutral-900">{option.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Identity Verification Note */}
        <div className="bg-warning-50 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-warning-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-warning-900">Identity Verification</h3>
              <p className="text-xs text-warning-800 mt-1">
                We will send a verification code to confirm your phone number(s)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex space-x-3 mt-8">
        <Button
          onClick={onBack}
          variant="secondary"
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          className="flex-1"
        >
          Continue →
        </Button>
      </div>
    </div>
  );
};

export default CreateAccountScreen;
